import React, { useState } from 'react';
import { ChevronDown, ChevronUp, FileText, GripVertical, Plus, Trash2 } from 'lucide-react';
import {
  DEFAULT_ACCEPTED_FORMATS,
  DOCUMENT_VALIDATION_RULES,
  createDocument,
  type DocumentValidationRule,
  type ProgramDocument,
} from '../../../lib/programConfig';
import { RichTextEditor } from '../../shared/RichTextEditor';
import { dragRowClass, moveItem, useDragReorder } from './dragReorder';

/**
 * Required Documents editor.
 *
 * Before: `requiredDocuments` was a `string[]` of labels. Administrators could
 * not add guidance, mark a document optional, restrict file types, set
 * validation rules or control the order — the brief asks for all of it.
 */

const FORMAT_CHOICES = ['pdf', 'jpg', 'jpeg', 'png', 'heic', 'doc', 'docx', 'xls', 'xlsx', 'zip'];

interface Props {
  documents: ProgramDocument[];
  onChange: (documents: ProgramDocument[]) => void;
  disabled?: boolean;
}

export default function RequiredDocumentsEditor({ documents, onChange, disabled }: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const commit = (next: ProgramDocument[]) =>
    onChange(next.map((doc, index) => ({ ...doc, sortOrder: index })));

  const reorder = (from: number, to: number) => commit(moveItem(documents, from, to));
  const drag = useDragReorder(reorder);

  const update = (id: string, patch: Partial<ProgramDocument>) =>
    commit(documents.map((doc) => (doc.id === id ? { ...doc, ...patch } : doc)));

  const remove = (id: string) => commit(documents.filter((doc) => doc.id !== id));

  const add = () => {
    const doc = createDocument('');
    commit([...documents, doc]);
    setExpandedId(doc.id);
  };

  const toggleFormat = (doc: ProgramDocument, format: string) => {
    const has = doc.acceptedFormats.includes(format);
    const next = has
      ? doc.acceptedFormats.filter((f) => f !== format)
      : [...doc.acceptedFormats, format];
    update(doc.id, { acceptedFormats: next.length ? next : [...DEFAULT_ACCEPTED_FORMATS] });
  };

  const toggleRule = (doc: ProgramDocument, rule: DocumentValidationRule) => {
    const has = doc.validationRules.includes(rule);
    update(doc.id, {
      validationRules: has
        ? doc.validationRules.filter((r) => r !== rule)
        : [...doc.validationRules, rule],
    });
  };

  return (
    <section className="space-y-3">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900">Required documents</h3>
          <p className="text-xs text-slate-500">
            Drag to reorder. This list drives the applicant upload checklist, admin review and
            document validation for this program only.
          </p>
        </div>
        <button
          type="button"
          onClick={add}
          disabled={disabled}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-xl btn-primary px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" /> Add document
        </button>
      </header>

      {documents.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center">
          <FileText className="mx-auto mb-2 h-8 w-8 text-slate-300" />
          <p className="text-sm font-semibold text-slate-700">No documents configured</p>
          <p className="mt-1 text-xs text-slate-500">
            Applicants will not be asked to upload anything for this program.
          </p>
        </div>
      )}

      <ul className="space-y-2">
        {documents.map((doc, index) => {
          const itemProps = drag.getItemProps(index);
          const expanded = expandedId === doc.id;
          return (
            <li
              key={doc.id}
              {...itemProps}
              className={dragRowClass(itemProps['data-dragging'], itemProps['data-drag-over'])}
            >
              <div className="flex items-start gap-2 p-3">
                <span
                  className="mt-1.5 cursor-grab text-slate-300 active:cursor-grabbing"
                  aria-hidden="true"
                >
                  <GripVertical className="h-4 w-4" />
                </span>

                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <input
                      value={doc.label}
                      placeholder="Document name, e.g. Valid Passport"
                      disabled={disabled}
                      onChange={(event) => update(doc.id, { label: event.target.value })}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    />
                    <label className="inline-flex shrink-0 items-center gap-2 text-xs font-semibold text-slate-600">
                      <input
                        type="checkbox"
                        checked={doc.required}
                        disabled={disabled}
                        onChange={(event) => update(doc.id, { required: event.target.checked })}
                        className="rounded border-slate-300 text-brand focus:ring-brand-primary"
                      />
                      Required
                    </label>
                  </div>

                  <input
                    value={doc.description || ''}
                    placeholder="Short description shown under the document name"
                    disabled={disabled}
                    onChange={(event) => update(doc.id, { description: event.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                  />

                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold">
                      {doc.acceptedFormats.join(', ').toUpperCase() || 'ANY'}
                    </span>
                    {doc.maxSizeMb ? (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold">
                        max {doc.maxSizeMb} MB
                      </span>
                    ) : null}
                    {doc.validationRules.length > 0 && (
                      <span className="rounded-full bg-brand-active px-2 py-0.5 font-semibold text-brand">
                        {doc.validationRules.length} rule
                        {doc.validationRules.length === 1 ? '' : 's'}
                      </span>
                    )}
                    {!doc.required && (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-700">
                        Optional
                      </span>
                    )}
                  </div>

                  {expanded && (
                    <div className="space-y-4 rounded-xl bg-slate-50 p-3">
                      <div>
                        <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                          Accepted file formats
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {FORMAT_CHOICES.map((format) => {
                            const active = doc.acceptedFormats.includes(format);
                            return (
                              <button
                                key={format}
                                type="button"
                                disabled={disabled}
                                onClick={() => toggleFormat(doc, format)}
                                aria-pressed={active}
                                className={`rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase transition ${
                                  active
                                    ? 'border-blue-200 bg-brand-active text-brand'
                                    : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                                }`}
                              >
                                {format}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                          Max size (MB)
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={100}
                          value={doc.maxSizeMb ?? ''}
                          placeholder="10"
                          disabled={disabled}
                          onChange={(event) =>
                            update(doc.id, {
                              maxSizeMb: event.target.value ? Number(event.target.value) : undefined,
                            })
                          }
                          className="w-24 rounded-lg border border-slate-200 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                        />
                      </div>

                      <div>
                        <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                          Validation rules
                        </p>
                        <div className="grid gap-1.5 sm:grid-cols-2">
                          {DOCUMENT_VALIDATION_RULES.map((rule) => (
                            <label
                              key={rule.id}
                              className="flex items-center gap-2 text-xs text-slate-700"
                            >
                              <input
                                type="checkbox"
                                checked={doc.validationRules.includes(rule.id)}
                                disabled={disabled}
                                onChange={() => toggleRule(doc, rule.id)}
                                className="rounded border-slate-300 text-brand focus:ring-brand-primary"
                              />
                              {rule.label}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                          Upload instructions
                        </p>
                        <RichTextEditor
                          value={doc.instructions || ''}
                          onChange={(html) => update(doc.id, { instructions: html })}
                          placeholder="Explain exactly what the applicant must upload…"
                          minHeight={110}
                          compact
                          disabled={disabled}
                          ariaLabel={`Instructions for ${doc.label || 'document'}`}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex shrink-0 flex-col items-center gap-0.5">
                  <button
                    type="button"
                    aria-label="Move up"
                    title="Move up"
                    disabled={disabled || index === 0}
                    onClick={() => reorder(index, index - 1)}
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label="Move down"
                    title="Move down"
                    disabled={disabled || index === documents.length - 1}
                    onClick={() => reorder(index, index + 1)}
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : doc.id)}
                    aria-expanded={expanded}
                    className="rounded-lg px-2 py-1 text-[11px] font-bold text-brand hover:bg-brand-active"
                  >
                    {expanded ? 'Done' : 'Configure'}
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove ${doc.label || 'document'}`}
                    disabled={disabled}
                    onClick={() => remove(doc.id)}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
